import React, { useEffect, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BarChart from "../../components/Charts/BarChart";
import LineChart from "../../components/Charts/LineChart";
import toast from "react-hot-toast";
import PieChart from "../../components/Charts/PieChart";
import { Card, Typography, CardBody, Chip } from "@material-tailwind/react";
import DashboardSkeletonLoading from "../../components/Loaders/DashboardSkeletonLoading";
import { getDashboardData } from "../../helperFunctions/apiFunction";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableRowWithShowMore from "../../components/TableRowWithShowMore";
import { setLoginStatus, setUserInfo, setRes } from "../../redux/dataSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import {
  barChartData,
  lineChartData,
  pieChartData,
} from "../../components/DummyData";

const AnalyticsPage = () => {
  const { refreshToken } = useAuthUserToken();
  const [top10Data, setTop10Data] = useState([]);
  const [severityTable, setSeverityTable] = useState();
  const [isLoading, setIsLoading] = useState(null);
  const [totalTop10ScanCount, setTotalTop10ScanCount] = useState(0);
  const [isDataSet, setIsDataSet] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userData, setUserData] = useState({
    labels: [],
    datasets: [],
  });
  const [assetsData, setAssetsData] = useState({
    labels: [],
    datasets: [],
  });
  const [totalGrantedData, setTotalGrantedData] = useState({
    labels: [],
    datasets: [],
  });
  const [last30DaysOkAndFailData, setLast30DaysOkAndFailData] = useState({
    labels: [],
    datasets: [],
  });

  const TABLE_HEAD = ["Category", "Count", "Severity"];

  const [dashData, setDashData] = useState({
    "Total Assets": 0,
    "Total Issues": 0,
    "Total Scans": 0,
    "Total Accounts": 0,
  });

  const generateRandomRgba = () => {
    const red = Math.floor(Math.random() * 256);
    const green = Math.floor(Math.random() * 256);
    const blue = Math.floor(Math.random() * 256);
    const alpha = Math.random().toFixed(1);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const generateRandomRgbaArray = (count) => {
    const rgbaArray = [];
    for (let i = 0; i < count; i++) {
      rgbaArray.push(generateRandomRgba());
    }
    return rgbaArray;
  };

  useEffect(() => {
    if (!isDataSet) {
      getDashboardData(refreshToken)
        .then((data) => {
          setIsLoading(false);
          if (data?.code === 200 || data?.code === 201) {
            setIsDataSet(true);
            const totalGrantedData = data?.totalGrantedData;
            const last30DaysOkAndFailData = data?.last30DaysOkAndFailData;
            const assetData = data?.data?.totalAsset;
            const issueData = data?.data?.totalIssues;
            const totalScanData = data?.data?.totalCompletedScanDataCount || 0;
            const totalAccountData =
              data?.data?.totalActiveIntegrationAccounts || 0;
            const top10ScanResultData = data?.failedScanResultDataCount;
            const issuesbySeverity = data?.issuesbySeverity;
            const totalIntegrationProvider = data?.groupedIntegrationProviders;
            let totalDataCount = 0;
            assetData.forEach((item) => {
              if (item?.total_data_count) {
                totalDataCount += item.total_data_count;
              }
            });

            let totalSeverityCount = 0;
            issueData.forEach((item) => {
              totalSeverityCount += item?.criticalSeverity || 0;
              totalSeverityCount += item?.highSeverity || 0;
              totalSeverityCount += item?.mediumSeverity || 0;
              totalSeverityCount += item?.lowSeverity || 0;
            });

            console.log("🚀 ~ .then ~ totalSeverityCount:", totalSeverityCount);

            let totalTop10ScanCount = 0;
            if (Array.isArray(top10ScanResultData)) {
              top10ScanResultData.forEach((item) => {
                if (item?.count) {
                  totalTop10ScanCount += item?.count || 0;
                }
              });
            }

            setDashData({
              "Total Assets": totalDataCount,
              "Total Issues": totalSeverityCount,
              "Total Scans": totalScanData,
              "Total Accounts": totalAccountData,
            });
            setTop10Data(top10ScanResultData);
            setTotalTop10ScanCount(totalTop10ScanCount);
            setSeverityTable(issuesbySeverity);
            setUserData({
              labels: totalIntegrationProvider.map((item) => item?._id),
              datasets: [
                {
                  label: "Integration Provider",
                  data: totalIntegrationProvider.map((item) => item?.count),
                  borderColor: "black",
                  // Adding gradient colors
                  fill: true,
                  backgroundColor: [
                    "rgba(201, 75, 75, 0.6)",
                    "rgba(75, 19, 79, 0.6)",
                    "rgba(15, 32, 39, 0.6)",
                    "rgba(238, 156, 167, 0.6)",
                    "rgba(131, 96, 195, 0.6)",
                    "rgba(128, 0, 128, 0.6)",
                  ],
                  // Define the gradient for each dataset
                  backgroundGradientFrom: "#ee9ca7",
                  backgroundGradientTo: "#4b134f",
                  // Adjust this to set the spacing of the gradient
                  borderWidth: 1,
                },
              ],
            });

            setAssetsData({
              labels: assetData.map((item) => item?._id),
              datasets: [
                {
                  label: "Total Asset",
                  data: assetData.map((item) => item?.total_data_count || 0),
                  borderColor: "black",
                  // backgorundColor: generateRandomRgbaArray(assetData.length),
                  backgroundColor: [
                    "rgba(201, 75, 75, 0.6)",
                    "rgba(75, 19, 79, 0.6)",
                    "rgba(15, 32, 39, 0.6)",
                    "rgba(238, 156, 167, 0.6)",
                    "rgba(131, 96, 195, 0.6)",
                    "rgba(128, 0, 128, 0.6)",
                  ],
                  backgroundGradientFrom: "#ee9ca7",
                  backgroundGradientTo: "#4b134f",
                  fill: true,
                  borderWidth: 1,
                },
              ],
            });

            // Granted Data Logic
            setTotalGrantedData({
              labels: totalGrantedData.map((item) => item?.month),
              datasets: [
                {
                  label: "Total Granted Data",
                  data: totalGrantedData.map((item) => item?.count || 0),
                  borderColor: "black",
                  backgroundColor: [
                    "rgba(201, 75, 75, 0.6)",
                    "rgba(75, 19, 79, 0.6)",
                    "rgba(15, 32, 39, 0.6)",
                    "rgba(238, 156, 167, 0.6)",
                    "rgba(131, 96, 195, 0.6)",
                    "rgba(128, 0, 128, 0.6)",
                  ],
                  backgroundGradientFrom: "#ee9ca7",
                  backgroundGradientTo: "#4b134f",
                  fill: true,
                  borderWidth: 1,
                },
              ],
            });

            // Last 30Days Ok And Failed Data
            setLast30DaysOkAndFailData({
              labels: Object.keys(last30DaysOkAndFailData),
              datasets: [
                {
                  label: "Total Failed Granted Data",
                  data: Object.keys(last30DaysOkAndFailData).map(
                    (key) => last30DaysOkAndFailData[key].failedDataCount || 0
                  ),
                  borderColor: "black",
                  backgroundColor: [
                    "rgba(201, 75, 75, 0.6)",
                    "rgba(75, 19, 79, 0.6)",
                    "rgba(15, 32, 39, 0.6)",
                    "rgba(238, 156, 167, 0.6)",
                    "rgba(131, 96, 195, 0.6)",
                    "rgba(128, 0, 128, 0.6)",
                  ],
                  backgroundGradientFrom: "#ee9ca7",
                  backgroundGradientTo: "#4b134f",
                  fill: true,
                  borderWidth: 1,
                },
                {
                  label: "Total OK Granted Data",
                  data: Object.keys(last30DaysOkAndFailData).map(
                    (key) => last30DaysOkAndFailData[key].okDataCount || 0
                  ),
                  // last30DaysOkAndFailData.map(
                  //   (item) => item?.failedDataCount || 0
                  // ),
                  borderColor: "black",
                  backgroundColor: [
                    "rgba(201, 75, 75, 0.6)",
                    "rgba(75, 19, 79, 0.6)",
                    "rgba(15, 32, 39, 0.6)",
                    "rgba(238, 156, 167, 0.6)",
                    "rgba(131, 96, 195, 0.6)",
                    "rgba(128, 0, 128, 0.6)",
                  ],
                  backgroundGradientFrom: "#ee9ca7",
                  backgroundGradientTo: "#4b134f",
                  fill: true,
                  borderWidth: 1,
                },
              ],
            });
          } else if (data?.code === 440) {
            toast.error(data?.message);
            dispatch(setLoginStatus({ status: false }));
            dispatch(setUserInfo());
            dispatch(setRes());
            localStorage.removeItem("access_token");
            localStorage.removeItem("rbac");
            localStorage.removeItem("user_info");
            navigate("/");
          } else {
            toast.error(data?.message);
          }
        })
        .catch((error) => {
          // toast.error(error);
          console.error("Error fetching dashboard data:", error);
        });
    }
  }, []);

  return (
    <div className="app-main__inner !pl-20">
      <div
        className="p-12 mb-6 pt-6 rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <div className="text-[24px] pb-4 font-black text-black">
          Welcome to{" "}
          <span className="text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text relative">
            TrustSpike Compliance Management Platform!
          </span>
        </div>

        {isLoading === null ? (
          <>
            <DashboardSkeletonLoading />
          </>
        ) : (
          <>
            <div className="row">
              {Object.keys(dashData).map((item, i) => {
                return (
                  <DetailsCard
                    title={item}
                    value={Object.values(dashData)[i]}
                    key={i * 3}
                    index={i}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* table row-1 */}
        <div className="flex gap-10 flex-col sm:flex-row">
          <div className="w-full sm:w-5/12 ">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-8 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Issues By Severity
                </h2>
                <MoreVertIcon />
              </div>
              {isLoading === null ? (
                <SeverityLoading />
              ) : (
                severityTable &&
                severityTable.data &&
                Object.entries(severityTable.data).map(([severity, count]) => (
                  <div key={severity} className="my-3 px-3 flex">
                    <div
                      className={`border-l-4 ${
                        severity?.toLowerCase() === "critical"
                          ? "border-red-600"
                          : severity?.toLowerCase() === "high"
                          ? "border-pink-600"
                          : severity?.toLowerCase() === "medium"
                          ? "border-orange-600"
                          : severity?.toLowerCase() === "low"
                          ? "border-green-600"
                          : ""
                      } rounded-lg mr-3`}
                    ></div>
                    <div className="flex justify-items-stretch justify-between w-full">
                      <div className="flex flex-col h-auto">
                        <p className="!text-2xl !font-bold text-blue-gray-800">
                          {count || 0}
                        </p>
                        <p className="text-blue-gray-600">{severity}</p>
                      </div>
                      <div className="self-center ml-auto">
                        <Chip
                          variant="ghost"
                          size="sm"
                          value={
                            severityTable?.totalSeverity > 0
                              ? Math.round(
                                  (count / severityTable?.totalSeverity) * 100
                                ) + "%"
                              : 0 + "%"
                          }
                          color={
                            severity.toLowerCase() === "critical"
                              ? "red"
                              : severity.toLowerCase() === "high"
                              ? "pink"
                              : severity.toLowerCase() === "medium"
                              ? "orange"
                              : severity.toLowerCase() === "low"
                              ? "green"
                              : ""
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="w-full sm:w-7/12 shrink">
            <div className="border w-full border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-4 py-3 flex justify-between items-center">
                <div className="flex flex-col">
                  <div className="w-full overflow-hidden">
                    <h2 className=" font-bold text-base">
                      Top 10 Most Common Issues {"("}Critical and High{")"}
                    </h2>
                  </div>
                  <p className="text-blue-gray-400 text-sm">
                    {totalTop10ScanCount} issues
                  </p>
                </div>
                <MoreVertIcon className="self-center" />
              </div>
              <div>
                <Card>
                  <CardBody className="overflow-scroll px-3 p-0 h-[295px]">
                    <table className="w-full min-w-max table-auto text-left">
                      <thead>
                        <tr>
                          {TABLE_HEAD.map((head) => (
                            <th
                              key={head}
                              className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 w-[40%]"
                            >
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="flex items-center justify-between gap-2 font-normal leading-none opacity-70"
                              >
                                {head}
                              </Typography>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {isLoading === null ? (
                        <tbody>
                          <tr>
                            <td colSpan={TABLE_HEAD.length}>
                              <TableSkeleton />
                            </td>
                          </tr>
                        </tbody>
                      ) : (
                        <tbody>
                          {top10Data.length > 0 ? (
                            top10Data.map(({ _id, severity, count }, index) => {
                              const isLast = index === top10Data.length - 1;
                              const classes = isLast
                                ? "p-3"
                                : "p-3 border-b border-blue-gray-50";

                              return (
                                <tr key={`${_id}-${index}`}>
                                  <td className={classes}>
                                    <div className="flex items-center gap-3">
                                      <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-normal w-44"
                                      >
                                        <TableRowWithShowMore data={_id} />
                                      </Typography>
                                    </div>
                                  </td>
                                  <td className={classes}>
                                    <div className="flex items-center gap-3">
                                      <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-normal"
                                      >
                                        {count}
                                      </Typography>
                                    </div>
                                  </td>
                                  <td className={classes}>
                                    <div className="w-max">
                                      {severity ? (
                                        <Chip
                                          variant="ghost"
                                          size="sm"
                                          value={severity || "N/A"} // Fallback for `value`
                                          color={
                                            severity
                                              ? severity.toLowerCase() ===
                                                "critical"
                                                ? "red"
                                                : severity.toLowerCase() ===
                                                  "high"
                                                ? "pink"
                                                : severity.toLowerCase() ===
                                                  "medium"
                                                ? "orange"
                                                : severity.toLowerCase() ===
                                                  "low"
                                                ? "green"
                                                : "gray" // Default color for unmatched cases
                                              : "gray" // Default color when `severity` is null or undefined
                                          }
                                        />
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="3">
                                <div className="flex flex-col items-center w-full">
                                  <img
                                    quality={100}
                                    src="/assets/images/9276430.jpg"
                                    className="w-[180px] mix-blend-darken"
                                    alt="no-data-found"
                                  />
                                  <p className="text-[20px] font-medium">
                                    No Data Found!
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      )}
                    </table>
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* section-1 charts row-1 */}
        <div className="flex flex-col md:flex-row gap-10 my-10">
          {/* section-1 table */}
          <div className="s1-p1 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Total Cloud Accounts
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <BarChart chartData={barChartData} />
                ) : (
                  <BarChart chartData={userData} />
                )}
              </div>
            </div>
          </div>

          <div className="s1-p2 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Issues Open vs Resolved (Last 30 days)
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <LineChart chartData={lineChartData} />
                ) : (
                  <LineChart chartData={last30DaysOkAndFailData} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* section-2 chart row-2 */}
        <div className="flex gap-10 flex-col md:flex-row my-10">
          <div className="s3-p1 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Top 5 Asset Categories
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <PieChart chartData={pieChartData} />
                ) : (
                  <PieChart chartData={userData} />
                )}
              </div>
            </div>
          </div>
          <div className="s3-p2 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Total Assets
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <PieChart chartData={pieChartData} />
                ) : (
                  <PieChart chartData={assetsData} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* section-3 charts row-3 */}
        <div className="flex gap-10 flex-col md:flex-row my-10">
          <div className="s3-p1 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Issue Ageing
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <BarChart chartData={barChartData} />
                ) : (
                  <BarChart chartData={userData} />
                )}
              </div>
            </div>
          </div>
          <div className="s3-p2 w-full sm:w-1/2">
            <div className="border border-blue-gray-500 rounded-3xl bg-white">
              <div className="border-b border-blue-gray-100/50 px-3 py-4 flex justify-between items-center">
                <h2 className="truncate w-auto font-bold text-base">
                  Issues Exceptions Granted (Last 6 months)
                </h2>
                <MoreVertIcon />
              </div>
              <div className="my-3 px-3">
                {isLoading === null ? (
                  <LineChart chartData={lineChartData} />
                ) : (
                  <LineChart chartData={totalGrantedData} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;

const icons = [
  "/assets/card-icons/locker-iso-color.png",
  "/assets/card-icons/fav-folder-iso-color.png",
  "/assets/card-icons/sheild-iso-color.png",
  "/assets/card-icons/zoom-iso-color.png",
];

const renderIcon = (index) => {
  if (index >= 0 && index < icons.length) {
    return <img src={icons[index]} className="w-20 p-2" alt="" />;
  }
  return null;
};

const DetailsCard = ({ title, value, index }) => {
  return (
    <div className="col-md-6 col-xl-3">
      <div className="mb-3 rounded-2xl bg-white shadow-md">
        <div className="p-3">
          <div className="flex flex-row justify-between items-center">
            <div>
              <span className="text-2xl font-bold text-[#344767]">{value}</span>
              <h6 className="mb-0 font-medium text-sm pt-2 text-[#67748E]">
                {title}
              </h6>
            </div>
            <div className="bg-gray-100 rounded-3xl bg-gradient-to-r from-cyan-50 to-blue-50 p-1">
              {renderIcon(index)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SeverityLoading = () =>
  Array(4)
    .fill(0)
    .map((_, index) => (
      <div key={index} className="my-4 px-3 flex">
        <div className="border-l-4 bg-gray-400 animate-pulse rounded-lg" />
        <div className="flex justify-items-stretch justify-between w-full">
          <div className="flex flex-col h-auto ml-3">
            <p className="!text-2xl !font-bold text-blue-gray-800 animate-pulse">
              0
            </p>
            <div className="animate-pulse flex items-center justify-start w-16 h-2.5 bg-gray-400/60 shadow border-gray-400 rounded-3xl" />
          </div>
          <div className="animate-pulse self-center ml-auto">
            <Chip variant="ghost" size="sm" value="0%" color="blue-gray" />
          </div>
        </div>
      </div>
    ));
